import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Search,
  Upload,
  FileText,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Truck as TruckIcon,
  X,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const DOC_COLS = [
  { key: "insurance_expiry", label: "Insurance" },
  { key: "np_expiry", label: "NP" },
  { key: "fitness_expiry", label: "Fitness" },
  { key: "tax_expiry", label: "Tax" },
  { key: "pucc_expiry", label: "PUCC" },
  { key: "permit_expiry", label: "Permit" },
  { key: "rc_valid_till", label: "RC" },
];

const DOC_TYPES = [
  { key: "rc", label: "RC", expiry_field: "rc_valid_till" },
  { key: "insurance", label: "Insurance", expiry_field: "insurance_expiry" },
  { key: "permit", label: "Permit", expiry_field: "permit_expiry" },
  { key: "fitness", label: "Fitness", expiry_field: "fitness_expiry" },
  { key: "tax", label: "Tax", expiry_field: "tax_expiry" },
  { key: "pucc", label: "PUCC", expiry_field: "pucc_expiry" },
  { key: "np", label: "National Permit", expiry_field: "np_expiry" },
];

const daysUntil = (iso) => {
  if (!iso) return null;
  const t = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((t - today) / (1000 * 60 * 60 * 24));
};

const cellTone = (iso) => {
  const d = daysUntil(iso);
  if (d === null || d === undefined) return "bg-slate-100 text-slate-400";
  if (d < 0) return "bg-red-200 text-red-900 font-bold";
  if (d <= 15) return "bg-red-100 text-red-800 font-semibold";
  if (d <= 30) return "bg-amber-100 text-amber-800";
  return "bg-emerald-50 text-emerald-700";
};

export default function FleetCompliance() {
  const [trucks, setTrucks] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [editTruck, setEditTruck] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [viewTruck, setViewTruck] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [tres, ares] = await Promise.all([
        api.get("/logistics/trucks"),
        api.get("/logistics/trucks/expiry-alerts?days=15"),
      ]);
      setTrucks(tres.data || []);
      setAlerts(ares.data);
    } catch (e) {
      toast.error("Failed to load fleet data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    let r = trucks.filter((t) => t.active !== false);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((t) =>
        (t.truck_no || "").toLowerCase().includes(q) ||
        (t.make || "").toLowerCase().includes(q) ||
        (t.model_name || "").toLowerCase().includes(q)
      );
    }
    if (filter === "expired" || filter === "red") {
      r = r.filter((t) =>
        DOC_COLS.some((c) => {
          const d = daysUntil(t[c.key]);
          if (d === null) return false;
          if (filter === "expired") return d < 0;
          return d >= 0 && d <= 15;
        })
      );
    }
    return r;
  }, [trucks, search, filter]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        "/logistics/trucks/import-xlsx?dry_run=false",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.errors?.length || 0} errors`);
      if (data.errors?.length) console.warn("Import errors:", data.errors);
      refresh();
    } catch (err) {
      const d = err?.response?.data?.detail || err?.message;
      toast.error(`Import failed: ${d}`);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const openEdit = async (t) => {
    setEditTruck(t);
    setEditForm({
      ...t,
      min_fastag_balance: t.min_fastag_balance ?? "",
      seating_capacity: t.seating_capacity ?? "",
      unladen_weight: t.unladen_weight ?? "",
      gvw: t.gvw ?? "",
      load_capacity: t.load_capacity ?? "",
    });
    try {
      const { data } = await api.get(`/logistics/trucks/${t.id}/documents`);
      setDocs(data || []);
    } catch {
      setDocs([]);
    }
  };

  const saveEdit = async () => {
    if (!editTruck) return;
    try {
      const { truck_no, id, created_at, created_by, ...patch } = editForm;
      // Numeric fields
      ["min_fastag_balance", "seating_capacity", "unladen_weight", "gvw",
        "load_capacity"].forEach((k) => {
        if (patch[k] === "" || patch[k] === null) patch[k] = null;
        else patch[k] = Number(patch[k]);
      });
      await api.patch(`/logistics/trucks/${editTruck.id}`, patch);
      toast.success("Truck updated");
      setEditTruck(null);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.message;
      toast.error(`Save failed: ${d}`);
    }
  };

  const uploadDoc = async (e, docKey) => {
    const file = e.target.files?.[0];
    if (!file || !editTruck) return;
    setUploading(docKey);
    try {
      const fd = new FormData();
      fd.append("doc_type", docKey);
      fd.append("file", file);
      await api.post(`/logistics/trucks/${editTruck.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${docKey.toUpperCase()} uploaded`);
      const { data } = await api.get(`/logistics/trucks/${editTruck.id}/documents`);
      setDocs(data || []);
      refresh();
    } catch (err) {
      const d = err?.response?.data?.detail || err?.message;
      toast.error(`Upload failed: ${d}`);
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const deleteDoc = async (docId) => {
    if (!editTruck) return;
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/logistics/trucks/${editTruck.id}/documents/${docId}`);
      const { data } = await api.get(`/logistics/trucks/${editTruck.id}/documents`);
      setDocs(data || []);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="fleet-compliance-page" className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Logistics</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Fleet Compliance Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            All trucks + their RC / Insurance / Permit / Fitness / Tax / PUCC / NP expiry dates.
            Red &lt;15 days, Amber 16-30 days, Black expired.
          </p>
        </div>
        <div className="flex gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".xlsx"
              onChange={onImport}
              disabled={importing}
              className="hidden"
              data-testid="bulk-import-input"
            />
            <span className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50">
              <Upload className="h-4 w-4" />
              {importing ? "Importing..." : "Bulk Import XLSX"}
            </span>
          </label>
        </div>
      </div>

      {/* Alert banner */}
      {alerts && alerts.total_alerts > 0 && (
        <div data-testid="fleet-alert-banner" className="bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-red-900 text-sm">
              {alerts.expired > 0 && `${alerts.expired} document${alerts.expired > 1 ? "s" : ""} EXPIRED`}
              {alerts.expired > 0 && alerts.red > 0 && " · "}
              {alerts.red > 0 && `${alerts.red} expiring in 15 days`}
              {alerts.amber > 0 && ` · ${alerts.amber} expiring in 30 days`}
            </div>
            <div className="text-xs text-red-700 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {alerts.alerts.slice(0, 5).map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <span className="font-mono font-semibold">{a.truck_no}</span>
                  <span className="opacity-70">{a.doc_label}</span>
                  <span className={a.severity === "expired" ? "text-red-900 font-bold" : "text-red-700"}>
                    {a.severity === "expired" ? `expired ${Math.abs(a.days_remaining)}d ago` : `in ${a.days_remaining}d`}
                  </span>
                </span>
              ))}
              {alerts.alerts.length > 5 && <span>+ {alerts.alerts.length - 5} more</span>}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="fleet-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search truck no / make / model"
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          {[
            { id: "all", label: "All" },
            { id: "red", label: "Expiring (15d)" },
            { id: "expired", label: "Expired" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`fleet-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs rounded-md border transition ${
                filter === f.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-500 ml-auto">
          {filtered.length} of {trucks.filter((t) => t.active !== false).length} trucks
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full min-w-[1100px] text-xs" data-testid="fleet-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-3 py-2 text-left">Truck</th>
              <th className="px-3 py-2 text-left">Make / Model</th>
              {DOC_COLS.map((c) => (
                <th key={c.key} className="px-2 py-2 text-center min-w-[80px]">{c.label}</th>
              ))}
              <th className="px-3 py-2 text-left">FASTag</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">No trucks.</td></tr>
            ) : filtered.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono font-semibold cursor-pointer" onClick={() => setViewTruck(t)} data-testid={`view-truck-${t.truck_no}`}>
                  <div className="flex items-center gap-1.5 text-blue-700 hover:underline">
                    <TruckIcon className="h-3.5 w-3.5 text-slate-400" />
                    {t.truck_no}
                  </div>
                  {t.fuel_type && <div className="text-[10px] text-slate-400 font-sans uppercase">{t.fuel_type}</div>}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  <div className="text-slate-700">{t.make || "—"}</div>
                  <div className="text-slate-400">{t.model_name || ""}</div>
                </td>
                {DOC_COLS.map((c) => {
                  const v = t[c.key];
                  const d = daysUntil(v);
                  return (
                    <td key={c.key} className="px-1 py-1 text-center">
                      {v ? (
                        <div className={`rounded px-1.5 py-1 ${cellTone(v)}`}>
                          <div className="text-[10px]">{v}</div>
                          {d !== null && (
                            <div className="text-[9px] mt-0.5">
                              {d < 0 ? `${Math.abs(d)}d ago` : `${d}d`}
                            </div>
                          )}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-[11px]">
                  {t.fastag_no || t.fastag_tag_id ? (
                    <>
                      <div className="font-mono">{t.fastag_no || t.fastag_tag_id}</div>
                      {t.fastag_issuer && <div className="text-slate-400">{t.fastag_issuer}</div>}
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewTruck(t)}
                      className="h-7 text-[11px] border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(t)}
                      data-testid={`edit-truck-${t.truck_no}`}
                      className="h-7 text-[11px]"
                    >
                      Edit
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Details modal — read-only LorryInfo-style card */}
      <Dialog open={!!viewTruck} onOpenChange={(v) => !v && setViewTruck(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-truck-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle style={{ fontFamily: "Chivo" }}>Vehicle Details</DialogTitle>
              {viewTruck && (
                <span className="font-mono text-sm bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-md">
                  {viewTruck.truck_no}
                </span>
              )}
            </div>
            <DialogDescription className="text-xs sr-only">Compliance & technical specifications.</DialogDescription>
          </DialogHeader>

          {viewTruck && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {/* Basic Information */}
              <section>
                <h3 className="text-blue-600 font-semibold mb-3 text-sm">Basic Information</h3>
                <dl className="space-y-1.5 text-xs">
                  {[
                    ["Registration Date", viewTruck.registration_date],
                    ["Registration Authority", viewTruck.registration_authority],
                    ["Owner", viewTruck.owner_name],
                    ["Make", viewTruck.make],
                    ["Model", viewTruck.model_name],
                    ["Body Type", viewTruck.body_type],
                    ["Vehicle Class", viewTruck.vehicle_class],
                    ["Fuel Type", viewTruck.fuel_type],
                    ["Color", viewTruck.color],
                    ["Emission Norms", viewTruck.emission_norms],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 gap-2 py-1 border-b border-slate-50 last:border-0">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="text-slate-900 text-right">{v || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              {/* Validity & Compliance — color-coded chips */}
              <section>
                <h3 className="text-blue-600 font-semibold mb-3 text-sm">Validity & Compliance</h3>
                <dl className="space-y-1.5 text-xs">
                  {DOC_TYPES.map((d) => {
                    const v = viewTruck[d.expiry_field];
                    return (
                      <div key={d.key} className="grid grid-cols-2 gap-2 py-1 border-b border-slate-50 last:border-0 items-center">
                        <dt className="text-slate-500">{d.label} Valid Until</dt>
                        <dd className="text-right">
                          {v ? (
                            <span className={`inline-block px-2 py-1 rounded text-[11px] ${cellTone(v)}`}>{v}</span>
                          ) : <span className="text-slate-300">—</span>}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </section>

              {/* Technical Details */}
              <section>
                <h3 className="text-blue-600 font-semibold mb-3 text-sm">Technical Details</h3>
                <dl className="space-y-1.5 text-xs">
                  {[
                    ["Engine Number", viewTruck.engine_number],
                    ["Chassis Number", viewTruck.chassis_number],
                    ["Seating Capacity", viewTruck.seating_capacity],
                    ["Unladen Weight", viewTruck.unladen_weight ? `${viewTruck.unladen_weight} kg` : null],
                    ["GVW", viewTruck.gvw ? `${viewTruck.gvw} kg` : null],
                    ["Load Capacity", viewTruck.load_capacity ? `${viewTruck.load_capacity} kg` : null],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 gap-2 py-1 border-b border-slate-50 last:border-0">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="text-slate-900 text-right">{v || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              {/* Insurance & Other + FASTag */}
              <section>
                <h3 className="text-blue-600 font-semibold mb-3 text-sm">Insurance & FASTag</h3>
                <dl className="space-y-1.5 text-xs">
                  {[
                    ["Insurance Company", viewTruck.insurance_company],
                    ["Policy Number", viewTruck.policy_number],
                    ["Purchase Date", viewTruck.purchase_date],
                    ["Vehicle Status", viewTruck.status],
                    ["Last Checked", viewTruck.last_checked],
                    ["FASTag No.", viewTruck.fastag_no || viewTruck.fastag_tag_id],
                    ["FASTag Issuer", viewTruck.fastag_issuer],
                    ["FASTag Mobile", viewTruck.fastag_register_mobile],
                    ["Min Balance", viewTruck.min_fastag_balance ? `₹${viewTruck.min_fastag_balance}` : null],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 gap-2 py-1 border-b border-slate-50 last:border-0">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="text-slate-900 text-right">
                        {k === "Vehicle Status" && v ? (
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 font-semibold uppercase">{v}</span>
                        ) : (v || "—")}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={() => { const t = viewTruck; setViewTruck(null); if (t) openEdit(t); }}>
              Edit / Upload Docs
            </Button>
            <Button onClick={() => setViewTruck(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Docs dialog */}
      <Dialog open={!!editTruck} onOpenChange={(v) => !v && setEditTruck(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-truck-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>
              {editTruck?.truck_no} — Compliance & Documents
            </DialogTitle>
            <DialogDescription className="text-xs">
              Edit RC details, compliance expiry dates, FASTag info, and upload supporting documents.
            </DialogDescription>
          </DialogHeader>

          {editTruck && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Compliance dates */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-900 border-b border-slate-200 pb-1">Compliance Expiry Dates</h3>
                {DOC_TYPES.map((d) => (
                  <div key={d.key} className="grid grid-cols-3 gap-2 items-center">
                    <label className="text-xs text-slate-600">{d.label}</label>
                    <Input
                      type="date"
                      value={editForm[d.expiry_field] || ""}
                      onChange={(e) => setEditForm({ ...editForm, [d.expiry_field]: e.target.value })}
                      className="h-8 text-xs col-span-1"
                      data-testid={`field-${d.expiry_field}`}
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => uploadDoc(e, d.key)}
                        disabled={uploading === d.key}
                        className="hidden"
                        data-testid={`upload-${d.key}`}
                      />
                      <span className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] rounded border border-slate-200 hover:bg-slate-50 whitespace-nowrap">
                        <Upload className="h-3 w-3" />
                        {uploading === d.key ? "Uploading…" : "Upload"}
                      </span>
                    </label>
                  </div>
                ))}
              </div>

              {/* RC + FASTag details */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-900 border-b border-slate-200 pb-1">RC & Vehicle Details</h3>
                {[
                  ["make", "Make"],
                  ["model_name", "Model"],
                  ["body_type", "Body Type"],
                  ["vehicle_class", "Vehicle Class"],
                  ["fuel_type", "Fuel Type"],
                  ["color", "Color"],
                  ["registration_date", "Registration Date", "date"],
                  ["purchase_date", "Purchase Date", "date"],
                  ["owner_name", "Owner"],
                  ["seating_capacity", "Seating Capacity", "number"],
                  ["gvw", "GVW (kg)", "number"],
                  ["load_capacity", "Load Capacity (kg)", "number"],
                  ["engine_number", "Engine No."],
                  ["chassis_number", "Chassis No."],
                  ["insurance_company", "Insurance Co."],
                  ["policy_number", "Policy No."],
                ].map(([k, lbl, type]) => (
                  <div key={k} className="grid grid-cols-3 gap-2 items-center">
                    <label className="text-xs text-slate-600">{lbl}</label>
                    <Input
                      type={type || "text"}
                      value={editForm[k] ?? ""}
                      onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                      className="h-8 text-xs col-span-2"
                      data-testid={`field-${k}`}
                    />
                  </div>
                ))}

                <h3 className="font-semibold text-sm text-slate-900 border-b border-slate-200 pb-1 pt-2">FASTag</h3>
                {[
                  ["fastag_no", "FASTag No."],
                  ["fastag_issuer", "Issuer"],
                  ["fastag_register_mobile", "Registered Mobile"],
                  ["min_fastag_balance", "Min Balance (₹)", "number"],
                ].map(([k, lbl, type]) => (
                  <div key={k} className="grid grid-cols-3 gap-2 items-center">
                    <label className="text-xs text-slate-600">{lbl}</label>
                    <Input
                      type={type || "text"}
                      value={editForm[k] ?? ""}
                      onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                      className="h-8 text-xs col-span-2"
                      data-testid={`field-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploaded docs list */}
          {docs.length > 0 && (
            <div>
              <h3 className="font-semibold text-sm text-slate-900 border-b border-slate-200 pb-1 mb-2">Uploaded Documents ({docs.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-xs hover:bg-slate-50">
                    <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold uppercase">{d.doc_type}</div>
                      <div className="truncate text-slate-500">{d.file_name}</div>
                    </div>
                    <a href={d.file_url} target="_blank" rel="noreferrer" className="p-1 hover:bg-blue-50 rounded">
                      <Download className="h-3.5 w-3.5 text-blue-600" />
                    </a>
                    <button onClick={() => deleteDoc(d.id)} className="p-1 hover:bg-red-50 rounded">
                      <X className="h-3.5 w-3.5 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3 mt-2 sticky bottom-0 bg-white">
            <Button variant="outline" onClick={() => setEditTruck(null)} data-testid="edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} data-testid="edit-save" className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
